import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Conversation } from "@/types/conversation";
import { DBMessage, MessageMetadata } from "@/types/message";
import { addMessage } from "@/utils/api";
import { Dispatch, SetStateAction } from "react";

// Helper function to find the last AI message (similar to our edge function helper)
function findLastAIMessage(messages: DBMessage[]): DBMessage | null {
  if (!messages || messages.length === 0) return null;
  
  // Filter to only AI messages that aren't regenerated
  const aiMessages = messages
    .filter(msg => msg.sender_type === "ai" && !msg.is_regenerated)
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  
  return aiMessages.length > 0 ? aiMessages[0] : null;
}

export const usePasteSellerResponse = (
  id: string | undefined,
  generateResponseFn: (
    conversation: Conversation | null, 
    messages: DBMessage[], 
    sellerResponseContent?: string
  ) => Promise<DBMessage | null>
) => {
  const { toast } = useToast();
  const [isPasting, setIsPasting] = useState(false);
  const [isProcessingAI, setIsProcessingAI] = useState(false);

  const handlePasteSellerResponse = async (
    conversation: Conversation,
    messages: DBMessage[],
    content: string,
    setMessages: Dispatch<SetStateAction<DBMessage[]>>
  ): Promise<void> => {
    if (!conversation || !id) return;
    
    try {
      // Start the pasting process - analyzing seller response
      setIsPasting(true);
      
      // Find last AI message for context
      const lastAIMessage = findLastAIMessage(messages);
      console.log("Context detection:", lastAIMessage 
        ? `Found last AI message from ${new Date(lastAIMessage.created_at).toLocaleString()}`
        : "No previous AI message found");
      
      // Basic sentiment check for UI feedback only
      // The real analysis will happen in OpenAI
      const basicSentiment = content.toLowerCase().includes("refund") && 
                             (content.toLowerCase().includes("approved") || 
                              content.toLowerCase().includes("processed")) ? "positive" : "neutral";
      
      // Create message metadata with context reference if available
      const metadata: MessageMetadata = {
        sentiment: basicSentiment,
        // If we have a last AI message, reference it as context
        ...(lastAIMessage ? { relatedMessageId: lastAIMessage.id } : {})
      };
      
      // Add the seller response to the database with context-aware metadata
      const sellerMessage = await addMessage(id, "seller", content, undefined, metadata);
      
      // Immediately update UI with the seller message
      const messagesWithSeller = [...messages, sellerMessage];
      setMessages(messagesWithSeller);
      
      toast({
        title: "Seller response added",
        description: "Analyzing response and generating appropriate reply...",
      });
      
      // Transition to AI processing phase
      setIsPasting(false);
      setIsProcessingAI(true);
      
      // Keep the loading indicator visible
      const ensureLoadingIndicatorVisible = () => {
        const loadingMessageElement = document.querySelector('.message-loading');
        if (loadingMessageElement) {
          loadingMessageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      };
      
      // Call immediately and then set an interval to keep it visible
      ensureLoadingIndicatorVisible();
      const scrollInterval = setInterval(ensureLoadingIndicatorVisible, 1000);
      
      // Generate a response to the seller using OpenAI
      const aiMessage = await generateResponseFn(conversation, messagesWithSeller, content);
      
      // Clear the scroll interval
      clearInterval(scrollInterval);
      
      // If successfully generated, update UI with the AI message
      if (aiMessage) {
        // Add metadata to link this AI message with the seller message it's responding to
        const enhancedAiMessage = {
          ...aiMessage,
          metadata: {
            ...aiMessage.metadata,
            relatedMessageId: sellerMessage.id
          }
        };
        
        setMessages([...messagesWithSeller, enhancedAiMessage]);
        
        // Scroll to the newly generated AI message
        setTimeout(() => {
          const newMessageElement = document.getElementById(`message-${enhancedAiMessage.id}`);
          if (newMessageElement) {
            newMessageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
    } catch (error: any) {
      console.error("Error adding seller response:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to add seller response.",
      });
    } finally {
      setIsPasting(false);
      setIsProcessingAI(false);
    }
  };

  return {
    handlePasteSellerResponse,
    isPasting,
    isProcessingAI
  };
};
